/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState } from 'preact/hooks';
import { isPanButtonVisible, isSelectButtonVisible, isZoomButtonVisible } from './utils';
import { DragModeControls } from './DragModeControls';
import { isMobile } from '#utils/UNSAFE_visUtils/clientUtils';
import { Dimension } from '../../utils/UNSAFE_visTypes/common';

type Props = {
  isZoomScroll: boolean;
  dragMode: 'user' | 'select' | 'zoom' | 'pan' | 'off';
  isMultiSelection: boolean;
  plotAreaSpace: Dimension;
  chartWidth: number;
  isRtl: boolean;
};

export function useDragModeControls({
  chartWidth,
  plotAreaSpace,
  isRtl,
  isZoomScroll,
  isMultiSelection,
  dragMode
}: Props) {
  const isDeviceMobile = isMobile();

  const dragButtonDivRef = useRef<HTMLDivElement>(null);

  const showPanButton = isPanButtonVisible(isZoomScroll, dragMode, isDeviceMobile);
  const showZoomButton = isZoomButtonVisible(isZoomScroll, isDeviceMobile, dragMode);
  const showSelectButton = isSelectButtonVisible(
    isDeviceMobile,
    isMultiSelection,
    isZoomScroll,
    dragMode
  );

  const [userDragMode, setUserDragMode] = useState<'select' | 'zoom' | 'pan' | undefined>(
    isDeviceMobile && isZoomScroll ? 'pan' : undefined
  );

  const toggleButtonContent = (
    <DragModeControls
      position={{
        top: `${plotAreaSpace.y}px`,
        right: isRtl ? undefined : `${chartWidth - plotAreaSpace.x - plotAreaSpace.width}px`,
        left: isRtl ? `${plotAreaSpace.x}px` : undefined
      }}
      divRef={dragButtonDivRef}
      showSelectButton={showSelectButton}
      showZoomButton={showZoomButton}
      showPanButton={showPanButton}
      dragMode={userDragMode}
      onDragModeChange={setUserDragMode}
    />
  );

  return {
    dragButtonDivRef,
    toggleButtonContent,
    userDragMode
  };
}
